import React, { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import CashierApi from "../../utility/CashierApi";
import { notify } from "../../components/toastify";
import useOnlineStatus from "../../utility/offline/useOnlineStatus";
import {
  getQueue,
  retryFailed,
  subscribeQueue,
  syncSales,
} from "../../utility/offline/saleQueue";

const MAX_PRODUCT_PAGES = 40;

function OfflineBanner() {
  const online = useOnlineStatus();
  const queryClient = useQueryClient();
  const { fetchData, getDataById } = CashierApi();
  const [queue, setQueue] = useState(getQueue);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => subscribeQueue(() => setQueue(getQueue())), []);

  const sync = useCallback(
    async (manual = false) => {
      setSyncing(true);
      try {
        const result = await syncSales();
        if (result.synced > 0) {
          notify(
            `${result.synced} offline sale${result.synced > 1 ? "s" : ""} synced to your dashboard.`,
            "success"
          );
          queryClient.invalidateQueries({ queryKey: ["cart"] });
        }
        // Failed sales are retried every minute; only speak up when one newly
        // fails or the cashier asked.
        if (result.newlyFailed > 0 || (manual && result.failed > 0)) {
          const why = [...new Set(getQueue().filter((s) => s.status === "failed").map((s) => s.error).filter(Boolean))].join("; ");
          notify(`${result.failed} offline sale(s) could not be synced${why ? `: ${why}` : "."}`);
        } else if (result.failed === 0 && manual && result.stopped === "auth") {
          notify("Your session expired. Log in again to sync.");
        } else if (result.failed === 0 && manual && result.stopped) {
          notify("Couldn't reach the server. Will retry.");
        }
      } finally {
        setSyncing(false);
      }
    },
    [queryClient]
  );

  // Sync as soon as the connection returns, and keep trying while sales wait.
  const pending = queue.filter((s) => s.status === "pending").length;
  const failedCount = queue.filter((s) => s.status === "failed").length;
  useEffect(() => {
    if (!online || pending + failedCount === 0) return undefined;
    sync();
    const id = setInterval(sync, 60 * 1000);
    return () => clearInterval(id);
  }, [online, pending, failedCount, sync]);

  // While online, keep a copy of the catalogue on the device so the till can
  // keep selling (and searching) if the connection drops.
  useEffect(() => {
    if (!online) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const first = await fetchData("products", 1);
        const last = Math.min(first?.last_page || 1, MAX_PRODUCT_PAGES);
        for (let page = 2; page <= last && !cancelled; page += 1) {
          await fetchData("products", page);
        }
        const cats = await fetchData("categories", 1);
        for (const cat of Array.isArray(cats) ? cats : cats?.data || []) {
          if (cancelled) return;
          await getDataById("categories/products/", cat.id);
        }
      } catch {
        // Best effort: the till still works online.
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const failed = queue.filter((s) => s.status === "failed");
  // Why the server refused them, e.g. "Product is Out of Stock!".
  const reasons = [...new Set(failed.map((s) => s.error).filter(Boolean))].join("; ");
  if (online && queue.length === 0) return null;

  return (
    <div
      className={`w-full px-4 py-1 text-sm flex items-center justify-between gap-3 ${
        online ? "bg-blue-100 text-blue-900" : "bg-amber-200 text-amber-900"
      }`}
      role="status"
    >
      <span>
        {!online && "You're offline. Sales are saved on this device and sync when the internet returns."}
        {online && pending > 0 && (syncing ? "Syncing offline sales…" : `${pending} offline sale${pending > 1 ? "s" : ""} waiting to sync.`)}
        {!online && pending > 0 && ` ${pending} waiting.`}
        {online && failed.length > 0 && ` ${failed.length} failed${reasons ? `: ${reasons}` : "."}`}
      </span>
      <span className="flex gap-2">
        {online && pending > 0 && !syncing && (
          <button className="underline" onClick={() => sync(true)}>
            Sync now
          </button>
        )}
        {failed.length > 0 && online && (
          <button
            className="underline"
            onClick={retryFailed}
          >
            Retry failed
          </button>
        )}
      </span>
    </div>
  );
}

export default OfflineBanner;
